/**
 * Date entry modal for setting start and target dates on a Planning View
 * submission lane. Uses a fixed-position centered overlay so it's never
 * clipped by container boundaries (A2F-451).
 */

import { useState, useRef, useEffect } from 'react';

interface Props {
  startDate: string | null;
  targetDate: string | null;
  onSave: (startDate: string, targetDate: string) => void;
  onCancel: () => void;
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

export function DatePopover({ startDate, targetDate, onSave, onCancel }: Props) {
  const [start, setStart] = useState(startDate?.split('T')[0] ?? todayStr());
  const [target, setTarget] = useState(targetDate?.split('T')[0] ?? todayStr());
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onCancel]);

  const valid = start <= target;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" onClick={onCancel}>
      <div
        ref={ref}
        className="bg-white rounded-xl border border-slate-200 shadow-xl p-5 w-80"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-brand-charcoal mb-3">Set Dates</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange/30 outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
              Target Date
            </label>
            <input
              type="date"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange/30 outline-none"
            />
          </div>
        </div>

        {!valid && (
          <p className="text-[10px] text-red-500 mt-2">Start date must be before or equal to target date.</p>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            className="text-xs text-slate-400 hover:text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={() => valid && onSave(start, target)}
            disabled={!valid}
            className="px-3 py-1.5 text-xs font-medium text-white bg-brand-orange rounded-lg hover:bg-brand-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
